/**
 * Termo de uso, termo de aceite da proposta e os aceites (P-USU-2).
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3, e
 * docs/ADR/0037-termo-de-uso-e-termo-de-aceite.md.
 */

import { query, type ListParams } from './comum';

import { api, requestBlob, requestPage } from '../client';

export type TermsVersionStatus = 'rascunho' | 'ativa' | 'revogada';
export type TermsAcceptanceKind = 'emissao' | 'endosso' | 'cotacao';
export type TermsAudience = 'corretora' | 'todos';

/** Uma versao do termo de uso ou do termo de aceite da proposta. */
export interface TermsVersion {
  id: string;
  kind?: TermsAcceptanceKind;
  version: number;
  status: TermsVersionStatus;
  based_on_version: number | null;
  notes: string;
  title: string;
  /** HTML saneado pelo servidor. */
  body: string;
  body_sha256: string;
  body_chars: number;
  created_label: string;
  created_at: string;
  updated_label: string;
  updated_at: string;
  activated_label: string;
  activated_at: string | null;
  justification: string;
  effective_from: string | null;
  effective_until: string | null;
  revoked_label: string;
  revoked_at: string | null;
  revocation_reason: string;
}

/** A versao vigente como a pessoa a le. */
export interface TermsCurrentVersion {
  id: string;
  kind?: TermsAcceptanceKind;
  version: number;
  title: string;
  body: string;
  body_sha256: string;
  effective_from: string | null;
}

/** A situacao do termo de uso para a pessoa da sessao. */
export interface TermsOfUseCurrent {
  version: TermsCurrentVersion | null;
  acceptance: { id: string; accepted_at: string } | null;
  required: boolean;
  applies: boolean;
  must_accept: boolean;
  blocking: boolean;
}

export interface TermsBrokerRef {
  id: string;
  label: string;
}

export interface TermsAcceptance {
  id: string;
  terms_of_use_id: string;
  version: number;
  title: string;
  body_sha256: string;
  user_id: string | null;
  member_id: string | null;
  user_label: string;
  user_email: string;
  broker: TermsBrokerRef | null;
  accepted_at: string;
  ip: string;
  user_agent: string;
}

export interface TermsApproval {
  id: string;
  acceptance_term_id: string;
  kind: TermsAcceptanceKind;
  version: number;
  title: string;
  body_sha256: string;
  operation_id: string | null;
  endorsement_id: string | null;
  user_id: string | null;
  member_id: string | null;
  user_label: string;
  user_email: string;
  broker: TermsBrokerRef | null;
  accepted_at: string;
  ip: string;
  user_agent: string;
}

export interface TermsSettings {
  acceptance_required: boolean;
  audience: TermsAudience;
  updated_label: string;
  updated_at: string | null;
}

export interface TermsVersionInput {
  title?: string;
  body?: string;
  notes?: string;
}

const termoDeUso = '/api/v1/terms-of-use';
const termoDeAceite = '/api/v1/acceptance-terms';

export const termsOfUse = {
  current: (signal?: AbortSignal) =>
    api.get<TermsOfUseCurrent>(`${termoDeUso}/current`, signal),
  accept: (versionId: string) =>
    api.post<TermsAcceptance>(`${termoDeUso}/current/acceptance`, {
      version_id: versionId,
    }),
  list: (signal?: AbortSignal) => api.get<TermsVersion[]>(termoDeUso, signal),
  create: (input: TermsVersionInput) => api.post<TermsVersion>(termoDeUso, input),
  update: (versionId: string, input: TermsVersionInput) =>
    api.patch<TermsVersion>(`${termoDeUso}/${versionId}`, input),
  activate: (versionId: string, justification: string) =>
    api.post<TermsVersion>(`${termoDeUso}/${versionId}/activation`, { justification }),
  revoke: (versionId: string, reason: string) =>
    api.post<TermsVersion>(`${termoDeUso}/${versionId}/revocation`, { reason }),
  settings: (signal?: AbortSignal) =>
    api.get<TermsSettings>('/api/v1/settings/terms-of-use', signal),
  updateSettings: (
    input: Partial<Pick<TermsSettings, 'acceptance_required' | 'audience'>>,
  ) => api.patch<TermsSettings>('/api/v1/settings/terms-of-use', input),
};

export const termAcceptances = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<TermsAcceptance>(`/api/v1/term-acceptances${query(params)}`, signal),
  download: (acceptanceId: string) =>
    requestBlob(`/api/v1/term-acceptances/${acceptanceId}/download`),
};

export const acceptanceTerms = {
  current: (kind: TermsAcceptanceKind, signal?: AbortSignal) =>
    api.get<TermsCurrentVersion | null>(
      `${termoDeAceite}/current${query({ kind })}`,
      signal,
    ),
  list: (kind?: TermsAcceptanceKind, signal?: AbortSignal) =>
    api.get<TermsVersion[]>(`${termoDeAceite}${query({ kind })}`, signal),
  create: (kind: TermsAcceptanceKind, input: TermsVersionInput) =>
    api.post<TermsVersion>(termoDeAceite, { kind, ...input }),
  update: (termId: string, input: TermsVersionInput) =>
    api.patch<TermsVersion>(`${termoDeAceite}/${termId}`, input),
  activate: (termId: string, justification: string) =>
    api.post<TermsVersion>(`${termoDeAceite}/${termId}/activation`, { justification }),
  revoke: (termId: string, reason: string) =>
    api.post<TermsVersion>(`${termoDeAceite}/${termId}/revocation`, { reason }),
  approve: (
    termId: string,
    target: { operation_id: string } | { endorsement_id: string },
  ) => api.post<TermsApproval>(`${termoDeAceite}/${termId}/approvals`, target),
  approvals: (params: ListParams, signal?: AbortSignal) =>
    requestPage<TermsApproval>(
      `/api/v1/acceptance-term-approvals${query(params)}`,
      signal,
    ),
};
